import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import Box from "@mui/material/Box";
import {
  ALL_FILTER_VALUE,
  NOTIFICATION_TYPE_FILTER_OPTIONS,
  LIFECYCLE_FILTER_OPTIONS,
  SEVERITY_FILTER_OPTIONS,
} from "@/features/deployment-dashboard/widgets/panels/notification-filters/notification-filters-bar.utils";
import type { FilterOption } from "@/features/deployment-dashboard/widgets/panels/notification-filters/notification-filters-bar.utils";
import type { NotificationFeedFilters } from "@/features/deployment-dashboard/notifications/hooks/use-notifications-feed";

interface NotificationFiltersBarProps {
  filters: NotificationFeedFilters;
  onFiltersChange: (filters: NotificationFeedFilters) => void;
}

interface FilterFieldProps<T extends string> {
  id: string;
  label: string;
  value: T | typeof ALL_FILTER_VALUE;
  options: FilterOption<T>[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** Labeled Select control shared by the severity, category, and lifecycle filters. */
function FilterField<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
}: FilterFieldProps<T>) {
  const labelId = `${id}-label`;

  function handleChange(event: SelectChangeEvent) {
    onChange(event.target.value);
  }

  return (
    <FormControl sx={{ flex: "1 1 220px", minWidth: 0 }}>
      <FormLabel
        id={labelId}
        sx={{
          display: "block",
          mb: 0.5,
          fontSize: "0.625rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "text.secondary",
          "&.Mui-focused": { color: "text.secondary" },
        }}
      >
        {label}
      </FormLabel>
      <Select labelId={labelId} value={value} onChange={handleChange} disabled={disabled}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * Severity, notification type, and lifecycle status filter row for the notifications feed.
 */
function selectedValue<T extends string>(
  value: string,
  options: readonly FilterOption<T>[]
): T | null {
  const option = options.find((candidate) => candidate.value === value);
  return option?.value === ALL_FILTER_VALUE || option === undefined ? null : option.value;
}

export function NotificationFiltersBar({ filters, onFiltersChange }: NotificationFiltersBarProps) {
  const lifecycleDisabled = filters.notificationType === "system";

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <FilterField
        id="notification-severity-filter"
        label="Severity Level"
        value={filters.severity ?? ALL_FILTER_VALUE}
        options={SEVERITY_FILTER_OPTIONS}
        onChange={(value) =>
          onFiltersChange({
            ...filters,
            severity: selectedValue(value, SEVERITY_FILTER_OPTIONS),
          })
        }
      />
      <FilterField
        id="notification-type-filter"
        label="Notification Type"
        value={filters.notificationType ?? ALL_FILTER_VALUE}
        options={NOTIFICATION_TYPE_FILTER_OPTIONS}
        onChange={(value) =>
          onFiltersChange({
            ...filters,
            notificationType: selectedValue(value, NOTIFICATION_TYPE_FILTER_OPTIONS),
            lifecycleStatus:
              selectedValue(value, NOTIFICATION_TYPE_FILTER_OPTIONS) === "system"
                ? null
                : filters.lifecycleStatus,
          })
        }
      />
      <FilterField
        id="notification-lifecycle-filter"
        label="Lifecycle Status"
        value={filters.lifecycleStatus ?? ALL_FILTER_VALUE}
        options={LIFECYCLE_FILTER_OPTIONS}
        disabled={lifecycleDisabled}
        onChange={(value) =>
          onFiltersChange({
            ...filters,
            lifecycleStatus: selectedValue(value, LIFECYCLE_FILTER_OPTIONS),
          })
        }
      />
    </Box>
  );
}
